import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Close as CloseIcon, Done as DoneIcon } from '@x5-react-uikit/icons';
import { Button, Input } from '@x5-react-uikit/core';
import debounce from 'lodash/debounce';

import { CustomIcon } from 'components';
import { useDidMountEffect } from 'utils/hooks';

import {
  ActiveWrapper,
  ChipsWrapper,
  Circle,
  ConfirmWrapper,
  Container,
  FilterChip,
  InputsWrapper,
  SearchWrapper,
  ToggleContainer,
  ToggleFilters,
  TopWrapper,
} from './styles';

type Props = {
  isEnabled: boolean;
  isSearchEnabled?: boolean;
  activeFilters?: { key: string; name: string; value?: string }[];
  children: ReactElement;
  onConfirm: () => void;
  searchLabel?: string;
  searchValue?: string;
  onSearch: (query: string) => void;
  onReset: () => void;
  onDeleteOne: (key: string) => void;
};

const Filters: React.FC<Props> = ({
  children: filters,
  activeFilters,
  isEnabled,
  isSearchEnabled = true,
  searchLabel = 'Поиск по названию РК, id задания и т.д.',
  searchValue: initialSearchValue = '',
  onSearch,
  onConfirm,
  onReset,
  onDeleteOne,
}) => {
  const [toggle, setToggle] = useState(false);
  const [search, setSearch] = useState(initialSearchValue);

  const { current: debouncedOnSearch } = useRef(debounce(onSearch, 500));

  const handleConfirm = () => {
    onConfirm();
    setToggle(false);
  };

  const handleReset = (e: any) => {
    e.stopPropagation();

    onReset();
    setToggle(false);
  };

  useEffect(() => {
    setSearch(initialSearchValue);
  }, [initialSearchValue]);

  useDidMountEffect(() => {
    debouncedOnSearch(search);
  }, [search, debouncedOnSearch]);

  return (
    <Container>
      <TopWrapper>
        <ToggleFilters
          toggle={toggle}
          onClick={() => setToggle((prev) => !prev)}
        >
          Фильтрация
          <CustomIcon
            name="filter"
            width={21}
            height={21}
            color="rgba(108, 115, 125, 1)"
          />
          <ActiveWrapper>
            {(isEnabled || toggle) && (
              <>
                <Circle
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="7"
                    cy="7"
                    r="5.5"
                    fill="#1E88E5"
                    stroke={toggle ? '#E1E5EB' : '#eff1f5'}
                    strokeWidth="3"
                  />
                </Circle>

                <CloseIcon
                  size="small"
                  color="rgba(108, 115, 125, 1)"
                  style={{ verticalAlign: '-4px', cursor: 'pointer' }}
                  onClick={handleReset}
                />
              </>
            )}
          </ActiveWrapper>
        </ToggleFilters>
        {isSearchEnabled && (
          <SearchWrapper>
            <Input
              search
              label={searchLabel}
              value={search}
              onChange={({ target: { value } }) => setSearch(value)}
            />
          </SearchWrapper>
        )}
      </TopWrapper>
      <ToggleContainer toggle={toggle}>
        <InputsWrapper>{filters}</InputsWrapper>
        <ConfirmWrapper>
          <Button
            variant="text"
            size="medium"
            endIcon={
              <CloseIcon
                size="small"
                color="rgba(108, 115, 125, 1)"
                style={{ marginTop: '2px', marginLeft: '4px' }}
              />
            }
            onClick={handleReset}
          >
            Сбросить
          </Button>
          <Button
            variant="text"
            size="medium"
            endIcon={
              <DoneIcon
                size="small"
                color="rgba(21, 101, 192, 1)"
                style={{ marginTop: '2px', marginLeft: '4px' }}
              />
            }
            onClick={handleConfirm}
          >
            Применить фильтры
          </Button>
        </ConfirmWrapper>
      </ToggleContainer>
      {isEnabled && !toggle && (
        <ChipsWrapper>
          {activeFilters?.map((filter) => (
            <FilterChip key={filter.key}>
              {[filter.name, filter.value].filter(Boolean).join(': ')}
              <CloseIcon
                size="small"
                color="rgba(108, 115, 125, 1)"
                style={{
                  verticalAlign: 'sub',
                  marginLeft: '7px',
                  cursor: 'pointer',
                }}
                onClick={() => onDeleteOne(filter.key)}
              />
            </FilterChip>
          ))}
        </ChipsWrapper>
      )}
    </Container>
  );
};

export default Filters;
